import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { RootDispatch, RootState } from "../../store";
import ProductManagement from "../Products/ProductManagement";
import RefurbishedStockPage from "../Products/RefurbishedStockPage";
import ManageWarehouse from "./Management/ManageWarehouse";
import FloorInventoryPage from "./Floor/NewFloor";
import StoreInventoryPage from "./Store/NewStore";
import StockTransferTab from "./StockTransfer/StockTransferTab";
import { fetchProducts } from "../../store/slices/productSlice";
import { fetchWarehouseNamesAndIdsThunk } from "../../store/slices/warehouseSlice";

type TabKey =
  | "products"
  | "refurbished"
  | "management"
  | "store"
  | "floor"
  | "newStore"
  | "newfloor"
  | "stockTransfer";

const Warehouse: React.FC = () => {
  const dispatch = useDispatch<RootDispatch>();
  const role = useSelector((state: RootState) => state.auth.role);
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = (() => {
    const tab = searchParams.get("tab") as TabKey | null;
    return tab &&
      [
        "products",
        "refurbished",
        "management",
        "store",
        "floor",
        "newfloor",
        "newStore",
        "stockTransfer",
      ].includes(tab)
      ? tab
      : "products";
  })() as TabKey;

  const setTab = (key: TabKey) => {
    setSearchParams({ tab: key }, { replace: true });
  };

  useEffect(() => {
    if (activeTab === "refurbished") {
      dispatch(fetchProducts());
      dispatch(fetchWarehouseNamesAndIdsThunk());
    }
  }, [activeTab, dispatch]);

  const tabs = useMemo(() => {
    const base = [{ key: "products" as TabKey, label: "Products" }];

    if (
      role === "admin" ||
      role === "factory" ||
      role === "bdm" ||
      role === "store"
    ) {
      base.push({ key: "management" as TabKey, label: "Management" });
    }
    if (role === "admin" || role === "store" || role === "factory") {
      base.push({ key: "store", label: "Store" });
    }
    if (
      role === "admin" ||
      role === "floor" ||
      role === "factory" ||
      role === "store"
    ) {
      base.push({ key: "floor", label: "Floor" });
    }
    if (role === "admin" || role === "store" || role === "factory") {
      base.push({ key: "stockTransfer", label: "Stock Transfer" });
    }
    base.push({ key: "refurbished", label: "Refurbished Stock" });

    return base;
  }, [role]);

  return (
    <div className="min-h-screen bg-slate-50 font-inter antialiased">
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <nav
          aria-label="Warehouse sections"
          className="mb-4 flex gap-1 overflow-x-auto border-b border-gray-200"
        >
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setTab(tab.key)}
              aria-current={activeTab === tab.key ? "page" : undefined}
              className={`min-h-11 shrink-0 whitespace-nowrap rounded-t-lg px-4 py-2 text-sm font-medium transition-all ${
                activeTab === tab.key
                  ? "text-blue-600 border-b-2 border-blue-500 bg-white"
                  : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {activeTab === "products" && <ProductManagement />}
        {activeTab === "refurbished" && <RefurbishedStockPage />}
        {activeTab === "management" &&
          (role === "admin" ||
            role === "factory" ||
            role === "bdm" ||
            role === "store") && <ManageWarehouse />}
        {/* {activeTab === "store" &&
          (role === "admin" || role === "store" || role === "factory") && (
            <StoreInwardTab />
          )}
        {activeTab === "floor" &&
          (role === "admin" || role === "floor" || role === "factory") && (
            <FloorInwardPage />
          )} */}
        {activeTab === "floor" &&
          (role === "admin" ||
            role === "floor" ||
            role === "factory" ||
            role === "store") && <FloorInventoryPage />}
        {activeTab === "store" &&
          (role === "admin" || role === "factory" || role === "store") && (
            <StoreInventoryPage />
          )}
        {activeTab === "stockTransfer" &&
          (role === "admin" || role === "factory" || role === "store") && (
            <StockTransferTab />
          )}
      </div>
    </div>
  );
};

export default Warehouse;
